import { useRef, useState } from 'react'
import { ImagePlus } from 'lucide-react'
import { Button } from '@/components/ui'
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization'
import {
  useDeleteOrganizationLogo,
  useUploadOrganizationLogo,
} from '@/features/organization/hooks/useOrganizationLogo'

const ACCEPTED_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024

export interface LogoFieldProps {
  disabled?: boolean
}

/**
 * Saves as soon as a file is picked, unlike the rest of this form: a file can't
 * sit in the draft values, and the page's Save button would suggest it can.
 */
export function LogoField({ disabled = false }: LogoFieldProps): React.ReactElement {
  const { data: organization } = useCurrentOrganization()
  const uploadLogo = useUploadOrganizationLogo()
  const deleteLogo = useDeleteOrganizationLogo()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const logo = organization?.branding?.logo
  const isBusy = uploadLogo.isPending || deleteLogo.isPending

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!ACCEPTED_LOGO_TYPES.includes(file.type as (typeof ACCEPTED_LOGO_TYPES)[number])) {
      setError('Please upload a JPG, PNG, or WebP image.')
      return
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError('Logo must be 2 MB or smaller.')
      return
    }

    setError(null)
    uploadLogo.mutate(file)
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/30">
        {logo ? (
          <img src={logo} alt="Organization logo" className="h-full w-full object-contain p-1.5" />
        ) : (
          <ImagePlus className="h-5 w-5 text-gray-400" aria-hidden />
        )}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES.join(',')}
            className="hidden"
            aria-label="Upload organization logo"
            onChange={handleSelect}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="whitespace-nowrap"
            disabled={disabled || isBusy}
            loading={uploadLogo.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {logo ? 'Replace logo' : 'Upload logo'}
          </Button>
          {logo ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="whitespace-nowrap"
              disabled={disabled || isBusy}
              loading={deleteLogo.isPending}
              onClick={() => {
                setError(null)
                deleteLogo.mutate()
              }}
            >
              Remove
            </Button>
          ) : null}
        </div>
        {error ? (
          <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : (
          <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
            JPG, PNG, or WebP up to 2 MB. Shown on your sign-in page, in the sidebar, and on
            payslips. Saves right away.
          </p>
        )}
      </div>
    </div>
  )
}

LogoField.displayName = 'LogoField'
